<script>
	import {flushSync, onMount} from 'svelte';

	let {
		productName = '',
		images = [],
		videos = []
	} = $props();

	const dialogId = 'product-gallery-dialog';
	const zoomHistoryStateKey = 'productGalleryZoom';
	const zoomFragment = `#${dialogId}`;
	const invokerTestButton = document.createElement('button');
	const supportsInvokerCommands = 'command' in invokerTestButton &&
		'commandForElement' in invokerTestButton;
	const reducedMotionQuery = typeof window.matchMedia === 'function'
		? window.matchMedia('(prefers-reduced-motion: reduce)')
		: null;
	const mobileGalleryQuery = typeof window.matchMedia === 'function'
		? window.matchMedia('(max-width: 629px)')
		: null;
	const hasImages = $derived(images.length > 0);
	const hasVideos = $derived(videos.length > 0);

	let galleryElement = $state();
	let dialogElement = $state();
	let selectedMedia = $state(getInitialMedia());
	let selectedIndex = $state(0);
	let dialogSelectedIndex = $state(0);
	let playingVideoIndex = $state(null);
	let isZoomOpen = $state(false);
	let isViewTransitioning = $state(false);
	let activeViewTransitionCount = 0;
	let historyClosePending = false;
	let swipePointerId = null;
	let swipeScope = null;
	let swipeStartX = 0;
	let swipeStartY = 0;

	function getInitialMedia() {
		return images.length > 0 ? 'image' : 'video';
	}

	onMount(() => {
		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	});

	function mediaTransition(node, {duration = 260} = {}) {
		return {
			duration,
			css: progress => `opacity: ${progress}; transform: scale(${.985 + (.015 * progress)})`
		};
	}

	function selectMedia(media, index) {
		selectedMedia = media;
		selectedIndex = index;
		playingVideoIndex = null;
	}

	function selectRelative(delta, scope = 'normal') {
		if(scope === 'dialog') {
			const nextIndex = wrapIndex(dialogSelectedIndex + delta, images.length);
			selectDialogImage(nextIndex);
			return;
		}

		const mediaItems = selectedMedia === 'image' ? images : videos;
		selectMedia(selectedMedia, wrapIndex(selectedIndex + delta, mediaItems.length));
	}

	function wrapIndex(index, length) {
		return (index + length) % length;
	}

	function beginSwipe(event, scope) {
		if(!mobileGalleryQuery?.matches || images.length < 2) {
			return;
		}

		swipePointerId = event.pointerId;
		swipeScope = scope;
		swipeStartX = event.clientX;
		swipeStartY = event.clientY;
		event.currentTarget.setPointerCapture?.(event.pointerId);
	}

	function finishSwipe(event, scope) {
		if(event.pointerId !== swipePointerId || scope !== swipeScope) {
			return;
		}

		const distanceX = event.clientX - swipeStartX;
		const distanceY = event.clientY - swipeStartY;
		resetSwipe(event);

		if(Math.abs(distanceX) <= 60 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.5) {
			return;
		}

		event.preventDefault();
		selectRelative(distanceX < 0 ? 1 : -1, scope);
	}

	function cancelSwipe(event, scope) {
		if(event.pointerId === swipePointerId && scope === swipeScope) {
			resetSwipe(event);
		}
	}

	function resetSwipe(event) {
		if(event.currentTarget.hasPointerCapture?.(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		swipePointerId = null;
		swipeScope = null;
	}

	function selectDialogImage(index) {
		dialogSelectedIndex = index;
		flushSync();
		centerDialogThumbnail('smooth');
	}

	function centerDialogThumbnail(behavior) {
		const thumbnail = galleryElement?.querySelector(
			`[data-dialog-thumbnail-index="${dialogSelectedIndex}"]`
		);
		if(!thumbnail) {
			return;
		}

		const scrollThumbnail = () => {
			const isLandscape = window.matchMedia('(orientation: landscape)').matches;
			try {
				thumbnail.scrollIntoView({
					behavior,
					block: isLandscape ? 'center' : 'nearest',
					inline: isLandscape ? 'nearest' : 'center'
				});
			} catch(error) {
				thumbnail.scrollIntoView(false);
			}
		};

		if(isViewTransitioning) {
			scrollThumbnail();
		} else {
			window.requestAnimationFrame(scrollThumbnail);
		}
	}

	function shouldUseViewTransition() {
		return typeof document.startViewTransition === 'function' &&
			(!reducedMotionQuery || !reducedMotionQuery.matches);
	}

	function updateView(update) {
		if(!shouldUseViewTransition()) {
			update();
			return;
		}

		activeViewTransitionCount++;
		isViewTransitioning = true;
		flushSync();

		const transition = document.startViewTransition(() => {
			update();
			flushSync();
		});
		const finishTransition = () => {
			activeViewTransitionCount--;
			if(activeViewTransitionCount === 0) {
				isViewTransitioning = false;
			}
		};
		transition.finished.then(finishTransition, finishTransition);
	}

	function openZoom(event) {
		const shouldOpenWithJavaScript = !supportsInvokerCommands || shouldUseViewTransition();
		addZoomHistoryEntry();

		if(shouldOpenWithJavaScript) {
			event.preventDefault();
			updateView(openDialog);
			return;
		}

		dialogSelectedIndex = selectedIndex;
		isZoomOpen = true;
		flushSync();
		centerDialogThumbnail('smooth');
	}

	function openZoomForImage(event, index) {
		selectMedia('image', index);
		openZoom(event);
	}

	function openDialog() {
		dialogSelectedIndex = selectedIndex;
		isZoomOpen = true;
		flushSync();
		if(dialogElement && !dialogElement.open) {
			dialogElement.showModal();
		}
		centerDialogThumbnail('instant');
	}

	function closeDialog() {
		selectedMedia = 'image';
		selectedIndex = dialogSelectedIndex;
		isZoomOpen = false;
		flushSync();
		if(dialogElement?.open) {
			dialogElement.close();
		}
	}

	function isZoomHistoryEntry() {
		return window.location.hash === zoomFragment &&
			window.history.state &&
			window.history.state[zoomHistoryStateKey] === dialogId;
	}

	function addZoomHistoryEntry() {
		if(isZoomHistoryEntry() || typeof window.history.pushState !== 'function') {
			return;
		}

		const currentState = window.history.state && typeof window.history.state === 'object'
			? window.history.state
			: {};
		window.history.pushState(
			Object.assign({}, currentState, {[zoomHistoryStateKey]: dialogId}),
			'',
			zoomFragment
		);
		historyClosePending = false;
	}

	function closeViaHistory(event) {
		if(!isZoomHistoryEntry()) {
			return false;
		}

		event?.preventDefault();
		if(!historyClosePending) {
			historyClosePending = true;
			window.history.back();
		}

		return true;
	}

	function handleCloseButton(event) {
		if(!closeViaHistory(event) && shouldUseViewTransition()) {
			event.preventDefault();
			updateView(closeDialog);
		}
	}

	function handleCancel(event) {
		if(!closeViaHistory(event) && shouldUseViewTransition()) {
			event.preventDefault();
			updateView(closeDialog);
		}
	}

	function handleDialogKeydown(event) {
		if(event.key !== 'Escape') {
			return;
		}

		if(!closeViaHistory(event) && shouldUseViewTransition()) {
			event.preventDefault();
			updateView(closeDialog);
		}
	}

	function handleDialogClose() {
		selectedMedia = 'image';
		selectedIndex = dialogSelectedIndex;
		isZoomOpen = false;
		closeViaHistory();
	}

	function handlePopState() {
		historyClosePending = false;
		if(isZoomHistoryEntry()) {
			updateView(openDialog);
		} else if(dialogElement?.open) {
			updateView(closeDialog);
		}
	}

	function imageTransitionName(scope) {
		if(!isViewTransitioning) {
			return 'none';
		}

		return (scope === 'dialog') === isZoomOpen ? 'product-gallery-image' : 'none';
	}

	function thumbnailTransitionName(index, scope) {
		if(!isViewTransitioning || (scope === 'dialog') !== isZoomOpen) {
			return 'none';
		}

		return `product-gallery-thumbnail-${index + 1}`;
	}

	function navigationTransitionName(direction, scope) {
		if(!isViewTransitioning || (scope === 'dialog') !== isZoomOpen) {
			return 'none';
		}

		return `product-gallery-navigation-${direction}`;
	}
</script>

<div
	class="xn-product-gallery"
	class:is-view-transitioning={isViewTransitioning}
	class:is-image-mode={selectedMedia === 'image'}
	bind:this={galleryElement}
>
	<div class="stage">
		{#if selectedMedia === 'image' && images[selectedIndex]}
			{#key `image-${selectedIndex}`}
				<figure
					class="stage-item image-item"
					transition:mediaTransition={{duration: reducedMotionQuery?.matches ? 0 : 260}}
				>
					<button
						type="button"
						class="zoom-trigger"
						commandfor={dialogId}
						command="show-modal"
						onclick={openZoom}
						aria-label={`Bild ${selectedIndex + 1} von ${productName} vergrößern`}
						style={`view-transition-name: ${imageTransitionName('normal')}`}
					>
						<picture>
							<source
								media="(max-width: 629px)"
								srcset={images[selectedIndex].mobileImageSrcset || images[selectedIndex].mobileImageUrl}
							>
							<img
								src={images[selectedIndex].imageUrl}
								alt={images[selectedIndex].alt}
								width="1200"
								height="1200"
								decoding="async"
								loading={selectedIndex === 0 ? 'eager' : 'lazy'}
								fetchpriority={selectedIndex === 0 ? 'high' : 'auto'}
							>
						</picture>
					</button>
					{#if images.length > 1}
						<nav class="image-navigation" aria-label="Produktbild wechseln">
							<button
								type="button"
								onclick={() => selectRelative(-1)}
								aria-label="Vorheriges Bild anzeigen"
								style:view-transition-name={navigationTransitionName('previous', 'normal')}
								style:view-transition-class={isViewTransitioning ? 'product-gallery-navigation' : 'none'}
							>
								<span aria-hidden="true">&lsaquo;</span>
							</button>
							<button
								type="button"
								onclick={() => selectRelative(1)}
								aria-label="Nächstes Bild anzeigen"
								style:view-transition-name={navigationTransitionName('next', 'normal')}
								style:view-transition-class={isViewTransitioning ? 'product-gallery-navigation' : 'none'}
							>
								<span aria-hidden="true">&rsaquo;</span>
							</button>
						</nav>
					{/if}
				</figure>
			{/key}
		{:else if selectedMedia === 'video' && videos[selectedIndex]}
			{#key `video-${selectedIndex}`}
				<figure
					class="stage-item video-item"
					transition:mediaTransition={{duration: reducedMotionQuery?.matches ? 0 : 260}}
				>
					{#if playingVideoIndex === selectedIndex}
						<iframe
							src={`https://www.youtube.com/embed/${videos[selectedIndex]}?autoplay=1`}
							title={`Produktvideo ${selectedIndex + 1} von ${productName}`}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowfullscreen
							referrerpolicy="strict-origin-when-cross-origin"
						></iframe>
					{:else}
						<button
							type="button"
							class="video-start"
							onclick={() => playingVideoIndex = selectedIndex}
							aria-label={`Produktvideo ${selectedIndex + 1} abspielen`}
						>
							<img src={`https://img.youtube.com/vi/${videos[selectedIndex]}/0.jpg`} alt="">
							<span aria-hidden="true">▶</span>
						</button>
					{/if}
					{#if videos.length > 1}
						<nav class="image-navigation" aria-label="Produktvideo wechseln">
							<button type="button" onclick={() => selectRelative(-1)} aria-label="Vorheriges Video anzeigen">
								<span aria-hidden="true">&lsaquo;</span>
							</button>
							<button type="button" onclick={() => selectRelative(1)} aria-label="Nächstes Video anzeigen">
								<span aria-hidden="true">&rsaquo;</span>
							</button>
						</nav>
					{/if}
				</figure>
			{/key}
		{/if}
	</div>

	{#if selectedMedia === 'image'}
		<nav class="mobile-stage" aria-label="Produktbilder horizontal durchsuchen">
			{#each images as image, index}
				<figure class="mobile-stage-item">
					<button
						type="button"
						commandfor={dialogId}
						command="show-modal"
						onclick={event => openZoomForImage(event, index)}
						aria-label={`Bild ${index + 1} von ${productName} vergrößern`}
						style={`view-transition-name: ${selectedIndex === index ? imageTransitionName('normal') : 'none'}`}
					>
						<img
							src={image.mobileImageUrl || image.imageUrl}
							srcset={image.mobileImageSrcset}
							alt={image.alt}
							width="600"
							height="348"
							decoding="async"
							loading={index === 0 ? 'eager' : 'lazy'}
							fetchpriority={index === 0 ? 'high' : 'auto'}
						>
					</button>
				</figure>
			{/each}
		</nav>
	{/if}

	{#if hasImages && hasVideos}
		<nav class="media-navigation" aria-label="Medientyp auswählen">
			<button type="button" class:active={selectedMedia === 'image'} onclick={() => selectMedia('image', 0)}>
				Bilder
			</button>
			<button type="button" class:active={selectedMedia === 'video'} onclick={() => selectMedia('video', 0)}>
				Videos
			</button>
		</nav>
	{/if}

	{#if selectedMedia === 'image'}
		<nav class="thumbnails" aria-label="Produktbilder auswählen">
			{#each images as image, index}
				<button
					type="button"
					class="thumbnail"
					class:active={selectedIndex === index}
					onclick={() => selectMedia('image', index)}
					aria-label={`Bild ${index + 1} auswählen`}
					aria-pressed={selectedIndex === index}
					style:view-transition-name={thumbnailTransitionName(index, 'normal')}
					style:view-transition-class={isViewTransitioning ? 'product-gallery-thumbnail' : 'none'}
				>
					<img src={image.thumbnailUrl} alt="" width="96" height="96" loading="lazy" decoding="async">
				</button>
			{/each}
		</nav>
	{:else}
		<nav class="thumbnails" aria-label="Produktvideos auswählen">
			{#each videos as token, index}
				<button
					type="button"
					class="thumbnail video-thumbnail"
					class:active={selectedIndex === index}
					onclick={() => selectMedia('video', index)}
					aria-label={`Video ${index + 1} auswählen`}
					aria-pressed={selectedIndex === index}
				>
					<img src={`https://img.youtube.com/vi/${token}/0.jpg`} alt="" width="96" height="96" loading="lazy" decoding="async">
					<span aria-hidden="true">▶</span>
				</button>
			{/each}
		</nav>
	{/if}

	{#if hasImages}
		<dialog
			id={dialogId}
			class="zoom-dialog"
			bind:this={dialogElement}
			oncancel={handleCancel}
			onkeydown={handleDialogKeydown}
			onclose={handleDialogClose}
			aria-labelledby={`${dialogId}-title`}
		>
			<header>
				<strong id={`${dialogId}-title`}>{productName}</strong>
				<form method="dialog">
					<button type="submit" class="dialog-close" onclick={handleCloseButton} aria-label="Zoom schließen">
						<span aria-hidden="true">&times;</span>
					</button>
				</form>
			</header>
			<div class="dialog-content">
				<nav class="dialog-thumbnails" aria-label="Zoom-Bilder auswählen">
					{#each images as image, index}
						<button
							type="button"
							class="thumbnail"
							class:active={dialogSelectedIndex === index}
							onclick={() => selectDialogImage(index)}
							aria-label={`Bild ${index + 1} im Zoom auswählen`}
							aria-pressed={dialogSelectedIndex === index}
							data-dialog-thumbnail-index={index}
							style:view-transition-name={thumbnailTransitionName(index, 'dialog')}
							style:view-transition-class={isViewTransitioning ? 'product-gallery-thumbnail' : 'none'}
						>
							<img src={image.thumbnailUrl} alt="" width="96" height="96" loading="lazy" decoding="async">
						</button>
					{/each}
				</nav>
				<div
					class="dialog-stage"
					role="group"
					aria-label="Zoombild"
					onpointerdown={event => beginSwipe(event, 'dialog')}
					onpointerup={event => finishSwipe(event, 'dialog')}
					onpointercancel={event => cancelSwipe(event, 'dialog')}
				>
					{#key dialogSelectedIndex}
						<figure
							class="dialog-item"
							transition:mediaTransition={{duration: reducedMotionQuery?.matches ? 0 : 260}}
						>
							<picture>
								<source
									media="(max-width: 629px)"
									srcset={images[dialogSelectedIndex].mobileZoomUrl}
								>
								<img
									src={images[dialogSelectedIndex].zoomUrl}
									alt={images[dialogSelectedIndex].alt}
									width="2000"
									height="2000"
									loading="lazy"
									decoding="async"
									style={`view-transition-name: ${imageTransitionName('dialog')}`}
								>
							</picture>
							{#if images.length > 1}
								<nav class="image-navigation" aria-label="Zoombild wechseln">
									<button
										type="button"
										onclick={() => selectRelative(-1, 'dialog')}
										aria-label="Vorheriges Bild anzeigen"
										style:view-transition-name={navigationTransitionName('previous', 'dialog')}
										style:view-transition-class={isViewTransitioning ? 'product-gallery-navigation' : 'none'}
									>
										<span aria-hidden="true">&lsaquo;</span>
									</button>
									<button
										type="button"
										onclick={() => selectRelative(1, 'dialog')}
										aria-label="Nächstes Bild anzeigen"
										style:view-transition-name={navigationTransitionName('next', 'dialog')}
										style:view-transition-class={isViewTransitioning ? 'product-gallery-navigation' : 'none'}
									>
										<span aria-hidden="true">&rsaquo;</span>
									</button>
								</nav>
							{/if}
						</figure>
					{/key}
				</div>
			</div>
		</dialog>
	{/if}
</div>

<style>
	.xn-product-gallery {
		box-sizing: border-box;
		border-radius: .35em;
		background: #fff;
		box-shadow: 0 .2em .2em 0 rgba(17, 17, 17, .14), 0 .1em .5em 0 rgba(17, 17, 17, .12), 0 .2em .1em -.1em rgba(17, 17, 17, .2);
		user-select: none;
	}

	.xn-product-gallery,
	.xn-product-gallery * {
		box-sizing: border-box;
	}

	.stage {
		position: relative;
		width: 100%;
		aspect-ratio: 1 / 1;
		background: #fff;
	}

	.stage-item,
	.dialog-item {
		display: flex;
		position: absolute;
		inset: 0;
		margin: 0;
		align-items: center;
		justify-content: center;
		background: #fff;
	}

	.zoom-trigger,
	.video-start {
		display: flex;
		width: 100%;
		height: 100%;
		padding: 0;
		align-items: center;
		justify-content: center;
		border: 0;
		background: transparent;
	}

	.zoom-trigger {
		cursor: zoom-in;
	}

	.zoom-trigger > picture,
	.dialog-item > picture {
		display: block;
		width: 100%;
		height: 100%;
	}

	.zoom-trigger > picture > img,
	.dialog-item > picture > img,
	.video-start > img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.mobile-stage {
		display: none;
	}

	.video-item iframe {
		width: 100%;
		height: 100%;
		border: 0;
	}

	.video-start {
		position: relative;
		cursor: pointer;
	}

	.video-start > span,
	.video-thumbnail > span {
		display: flex;
		position: absolute;
		inset: 0;
		align-items: center;
		justify-content: center;
		color: #fff;
		font-size: 1.5em;
		text-shadow: 0 0 .2em #111;
	}

	.image-navigation {
		display: flex;
		position: absolute;
		z-index: 2;
		inset: 0;
		padding: 0 .75em;
		align-items: center;
		justify-content: space-between;
		pointer-events: none;
	}

	.image-navigation button {
		display: flex;
		width: 3em;
		height: 3em;
		padding: 0;
		align-items: center;
		justify-content: center;
		border: 1px solid rgba(17, 17, 17, .25);
		border-radius: 50%;
		background: rgba(255, 255, 255, .92);
		box-shadow: 0 .1em .4em rgba(17, 17, 17, .2);
		color: #111;
		font: inherit;
		line-height: 1;
		pointer-events: auto;
		cursor: pointer;
		transition: background-color .2s ease, box-shadow .2s ease, transform .2s ease;
	}

	.image-navigation button > span {
		margin-top: -.08em;
		font-size: 2.5em;
	}

	.media-navigation {
		display: flex;
		gap: .5em;
		padding-top: .75em;
	}

	.media-navigation button {
		padding: .45em .8em;
		border: 1px solid #bdbdbd;
		border-radius: .2em;
		background: #f8f8f8;
		color: #111;
		font: inherit;
		cursor: pointer;
	}

	.media-navigation button.active {
		border-color: #111;
		background: #fff;
	}

	.thumbnails,
	.dialog-thumbnails {
		display: flex;
		gap: .5em;
	}

	.thumbnails {
		flex-wrap: wrap;
		justify-content: center;
		padding-top: .75em;
		overflow: visible;
	}

	.thumbnail {
		display: block;
		position: relative;
		flex: 0 0 5em;
		width: 5em;
		height: 5em;
		padding: .15em;
		border: 2px solid transparent;
		background: #fff;
		cursor: pointer;
		scroll-snap-align: center;
		scroll-snap-stop: always;
	}

	.thumbnails > .thumbnail {
		flex-basis: 3.5em;
		width: 3.5em;
		height: 3.5em;
		scroll-snap-align: none;
		scroll-snap-stop: normal;
	}

	.thumbnail.active {
		border-color: #111;
	}

	.thumbnail > img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.thumbnail:focus-visible,
	.image-navigation button:focus-visible,
	.zoom-trigger:focus-visible,
	.dialog-close:focus-visible,
	.media-navigation button:focus-visible,
	.video-start:focus-visible {
		outline: 2px solid #111;
		outline-offset: 2px;
	}

	.zoom-dialog {
		grid-template-rows: auto minmax(0, 1fr);
		width: 100vw;
		height: 100vh;
		height: 100dvh;
		max-width: none;
		max-height: none;
		margin: 0;
		padding: 0;
		border: 0;
		background: #fff;
		color: #111;
	}

	.zoom-dialog[open] {
		display: grid;
	}

	.zoom-dialog::backdrop {
		background: rgba(17, 17, 17, .7);
	}

	.zoom-dialog > header {
		display: flex;
		min-width: 0;
		align-items: center;
		justify-content: space-between;
		gap: 1em;
		padding: 1em 1.25em;
		border-bottom: 1px solid #e0e0e0;
	}

	.zoom-dialog > header strong {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.zoom-dialog > header form {
		margin: 0;
	}

	.dialog-close {
		display: flex;
		width: 1.5em;
		height: 1.5em;
		padding: 0;
		align-items: center;
		justify-content: center;
		border: 0;
		background: transparent;
		color: #111;
		font: inherit;
		font-size: 2em;
		line-height: 1;
		cursor: pointer;
	}

	.dialog-content {
		display: grid;
		min-width: 0;
		min-height: 0;
		grid-template-rows: minmax(0, 1fr) auto;
	}

	.dialog-thumbnails {
		grid-row: 2;
		padding: .75em 1em 1em;
		overflow-x: auto;
		overflow-y: hidden;
		scroll-behavior: smooth;
		scroll-padding-inline: calc(50% - 2.5em);
		scroll-snap-type: x mandatory;
		scrollbar-width: thin;
	}

	.dialog-thumbnails::before,
	.dialog-thumbnails::after {
		content: '';
		flex: 0 0 calc(50% - 3em);
	}

	.dialog-stage {
		position: relative;
		min-width: 0;
		min-height: 0;
		grid-row: 1;
		background: #fff;
	}

	.dialog-item {
		padding: 1em;
	}

	@media (hover: hover) {
		.image-navigation button:hover {
			background: #fff;
			box-shadow: 0 .15em .55em rgba(17, 17, 17, .3);
			transform: scale(1.06);
		}
	}

	@media (orientation: landscape) {
		.dialog-content {
			grid-template-columns: 6.5em minmax(0, 1fr);
			grid-template-rows: minmax(0, 1fr);
		}

		.dialog-thumbnails {
			flex-direction: column;
			grid-column: 1;
			grid-row: 1;
			padding: 1em .75em;
			overflow-x: hidden;
			overflow-y: auto;
			scroll-padding-block: calc(50% - 2.5em);
			scroll-padding-inline: 0;
			scroll-snap-type: y mandatory;
		}

		.dialog-stage {
			grid-column: 2;
			grid-row: 1;
		}
	}

	@media (max-width: 629px) {
		.xn-product-gallery {
			box-shadow: none;
		}

		.stage {
			aspect-ratio: 50 / 29;
		}

		.dialog-stage {
			touch-action: pan-y pinch-zoom;
		}

		.xn-product-gallery.is-image-mode .stage {
			display: none;
		}

		.mobile-stage {
			display: flex;
			width: 100vw;
			margin-left: calc(50% - 50vw);
			overflow-x: auto;
			overflow-y: hidden;
			border-top: 1px solid #e0e0e0;
			border-bottom: 1px solid #e0e0e0;
			scroll-snap-type: x mandatory;
			-webkit-overflow-scrolling: touch;
		}

		.mobile-stage-item {
			position: relative;
			flex: 0 0 80vw;
			width: 80vw;
			height: 60vw;
			margin: 0;
			border-left: 1px solid #e0e0e0;
			background: #fff;
			scroll-snap-align: center;
		}

		.mobile-stage-item:first-child {
			border-left: 0;
		}

		.mobile-stage-item > button,
		.mobile-stage-item img {
			display: block;
			width: 100%;
			height: 100%;
		}

		.mobile-stage-item > button {
			padding: 0;
			border: 0;
			background: transparent;
		}

		.mobile-stage-item img {
			object-fit: contain;
			user-select: none;
			-webkit-user-drag: none;
		}

		.thumbnails,
		.stage .image-navigation,
		.dialog-thumbnails,
		.dialog-stage .image-navigation {
			display: none;
		}

		.zoom-dialog {
			grid-template-rows: minmax(0, 1fr);
		}

		.zoom-dialog > header {
			position: absolute;
			z-index: 4;
			top: 0;
			right: 0;
			padding: 10px;
			border: 0;
		}

		.zoom-dialog > header strong {
			position: absolute;
			width: 1px;
			height: 1px;
			padding: 0;
			margin: -1px;
			overflow: hidden;
			clip: rect(0, 0, 0, 0);
			white-space: nowrap;
			border: 0;
		}

		.dialog-close {
			width: 44px;
			height: 44px;
			border-radius: 50%;
			background: rgba(255, 255, 255, .9);
			box-shadow: 0 2px 12px rgba(17, 17, 17, .35);
		}

		.dialog-content {
			grid-template-columns: minmax(0, 1fr);
			grid-template-rows: minmax(0, 1fr);
		}

		.dialog-stage {
			grid-column: 1;
			grid-row: 1;
		}

		.dialog-item {
			padding: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.image-navigation button,
		.dialog-thumbnails {
			transition: none;
			scroll-behavior: auto;
		}
	}
</style>
